import { useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, ChevronDown, Image as ImageIcon } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { useServices, buildServiceBookingUrl } from '@/features/services'
import type { ServiceDocument } from '@/types'

type Service = ServiceDocument & { id: string }

export function ServicesSection() {
  const { services, categories: categoryDocs, loading } = useServices()
  const navigate = useNavigate()
  const [openCategory, setOpenCategory] = useState<string | null>(null)
  const [activeServiceId, setActiveServiceId] = useState<string | null>(null)
  // When the user clicks a service the rotation pauses on it; a second click
  // on the same service navigates to the booking page with it prefilled.
  const [pinnedServiceId, setPinnedServiceId] = useState<string | null>(null)
  const isHoveringRef = useRef(false)
  const [isHovering, setIsHovering] = useState(false)

  // Group services by category (same logic as ServicesPage)
  const { categories, byCategory } = useMemo(() => {
    const categoryOrder = new Map<string, number>()
    categoryDocs.forEach((c, i) => categoryOrder.set(c.title, i))

    const byCat: Record<string, Service[]> = {}
    const seen: string[] = []
    for (const service of services) {
      if (!byCat[service.category]) {
        byCat[service.category] = []
        seen.push(service.category)
      }
      byCat[service.category]!.push(service)
    }

    const ordered = seen.sort((a, b) => {
      const ai = categoryOrder.has(a) ? categoryOrder.get(a)! : Number.POSITIVE_INFINITY
      const bi = categoryOrder.has(b) ? categoryOrder.get(b)! : Number.POSITIVE_INFINITY
      if (ai !== bi) return ai - bi
      return seen.indexOf(a) - seen.indexOf(b)
    })

    return { categories: ordered, byCategory: byCat }
  }, [services, categoryDocs])

  // Initialize: open first category, make its first service active
  useEffect(() => {
    if (openCategory || categories.length === 0) return
    const first = categories[0]!
    setOpenCategory(first)
    setActiveServiceId(byCategory[first]?.[0]?.id ?? null)
  }, [categories, byCategory, openCategory])

  // Flat list of services in category-group order — drives the auto-cycle
  const orderedServices = useMemo(
    () => categories.flatMap((cat) => byCategory[cat] ?? []),
    [categories, byCategory]
  )

  // Auto-cycle through every service one at a time (pauses on hover).
  // When the next service is in a different category, open that category.
  useEffect(() => {
    if (loading || orderedServices.length < 2 || pinnedServiceId) return
    const interval = setInterval(() => {
      if (isHoveringRef.current) return
      setActiveServiceId((current) => {
        const idx = orderedServices.findIndex((s) => s.id === current)
        const next = orderedServices[(idx + 1) % orderedServices.length]!
        setOpenCategory(next.category)
        return next.id
      })
    }, 3000)
    return () => clearInterval(interval)
  }, [loading, orderedServices, pinnedServiceId])

  // Auto-release pin after 30s if the user isn't hovering the section.
  useEffect(() => {
    if (!pinnedServiceId || isHovering) return
    const t = setTimeout(() => setPinnedServiceId(null), 30000)
    return () => clearTimeout(t)
  }, [pinnedServiceId, isHovering])

  const handleServiceClick = (service: Service) => {
    if (pinnedServiceId === service.id) {
      navigate(buildServiceBookingUrl(service))
      return
    }
    setPinnedServiceId(service.id)
    setActiveServiceId(service.id)
    setOpenCategory(service.category)
  }

  const toggleCategory = (cat: string) => {
    if (openCategory === cat) {
      setOpenCategory(null)
    } else {
      setOpenCategory(cat)
      setActiveServiceId(byCategory[cat]?.[0]?.id ?? null)
    }
  }

  const activeService: Service | null =
    services.find((s) => s.id === activeServiceId) ?? null

  // Measure max panel height so the left column height stays constant
  // regardless of which category is open (taller category reserves space).
  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [maxPanelHeight, setMaxPanelHeight] = useState(0)

  useLayoutEffect(() => {
    const measure = () => {
      let max = 0
      for (const cat of categories) {
        const el = panelRefs.current[cat]
        if (el && el.scrollHeight > max) max = el.scrollHeight
      }
      setMaxPanelHeight(max)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [categories, byCategory])

  return (
    <section className="relative overflow-hidden border-y bg-background text-foreground py-24">

      <div className="container relative mx-auto px-4">
        <div
          className="grid grid-cols-1 gap-10 md:grid-cols-[1fr_2fr]"
          onMouseEnter={() => { isHoveringRef.current = true; setIsHovering(true) }}
          onMouseLeave={() => { isHoveringRef.current = false; setIsHovering(false) }}
        >
          {/* Left column — header, accordion, CTAs stacked */}
          <div className="flex flex-col">
            <div className="mb-10 max-w-2xl">
              <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-orange">
                Our Services
              </p>
              <h2 className="mb-2 text-4xl font-bold tracking-tight">
                High-quality media services.
              </h2>
              <p className="mb-4 text-lg font-medium text-muted-foreground">
                By dancers, for dancers.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                We know the art from the inside out. Every project helps fund new choreographic
                opportunities for emerging artists.
              </p>
            </div>

            <ul className="flex flex-col">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="border-t border-border py-6">
                  <Skeleton className="h-8 w-1/2 bg-muted" />
                </li>
              ))
            ) : (
              categories.map((cat, index) => {
                const isOpen = openCategory === cat
                const items = byCategory[cat] ?? []
                return (
                  <li
                    key={cat}
                    className={`border-t border-border ${
                      index === categories.length - 1 ? 'border-b' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-4 py-5 text-left"
                    >
                      <span className="flex items-baseline gap-4">
                        <span className="font-mono text-xs text-muted-foreground/60">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span
                          className={`text-xl md:text-2xl font-semibold tracking-tight leading-none transition-colors duration-300 ${
                            isOpen ? 'text-foreground' : 'text-muted-foreground/60'
                          }`}
                        >
                          {cat}
                        </span>
                        <span className="text-xs text-muted-foreground/60">
                          {items.length} offering{items.length === 1 ? '' : 's'}
                        </span>
                      </span>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 transition-transform duration-300 ${
                          isOpen ? 'rotate-180 text-brand-orange' : 'text-muted-foreground/60'
                        }`}
                      />
                    </button>

                    <div
                      className={`grid ease-in-out ${
                        isOpen
                          ? 'grid-rows-[1fr] opacity-100 transition-all duration-500'
                          : 'grid-rows-[0fr] opacity-0'
                      }`}
                      style={isOpen && maxPanelHeight ? { minHeight: maxPanelHeight } : undefined}
                    >
                      <div
                        ref={(el) => { panelRefs.current[cat] = el }}
                        className="overflow-hidden"
                      >
                        <ul className="mb-5 ml-10 flex flex-col gap-1 border-l border-border pl-5">
                          {items.map((service) => {
                            const isActive = activeServiceId === service.id
                            const isPinned = pinnedServiceId === service.id
                            return (
                              <li key={service.id}>
                                <button
                                  type="button"
                                  onClick={() => handleServiceClick(service)}
                                  onMouseEnter={() => {
                                    if (!pinnedServiceId) setActiveServiceId(service.id)
                                  }}
                                  onFocus={() => {
                                    if (!pinnedServiceId) setActiveServiceId(service.id)
                                  }}
                                  aria-label={
                                    isPinned
                                      ? `Click again to book ${service.title}`
                                      : service.title
                                  }
                                  className={`flex w-full items-center justify-between gap-3 rounded py-1.5 text-left text-sm transition-colors ${
                                    isActive
                                      ? 'text-brand-orange'
                                      : 'text-muted-foreground hover:text-foreground'
                                  }`}
                                >
                                  <span>{service.title}</span>
                                  {isPinned && (
                                    <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground/70">
                                      Click again to book
                                      <ArrowRight className="h-3 w-3" />
                                    </span>
                                  )}
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    </div>
                  </li>
                )
              })
            )}
            </ul>
          </div>

          {/* Right — image on top, text block below, both cross-fading */}
          <div className="flex h-full min-h-[28rem] flex-col gap-6 md:row-span-2">
            {/* Image row — real alpha fade at bottom so the section bg shows through */}
            <div
              className="relative flex-1 overflow-hidden rounded-t-2xl"
              style={{
                maskImage:
                  'linear-gradient(to bottom, black 0%, black 75%, transparent 100%)',
                WebkitMaskImage:
                  'linear-gradient(to bottom, black 0%, black 75%, transparent 100%)',
              }}
            >
              {loading || !activeService ? (
                <Skeleton className="absolute inset-0 bg-muted" />
              ) : (
                <>
                  {services.map((service) => {
                    const isActive = activeService.id === service.id
                    return service.photoUrl?.trim() ? (
                      <img
                        key={service.id}
                        src={service.photoUrl}
                        alt={service.title}
                        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                          isActive ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={{
                          objectPosition: `${(service.photoFocusX ?? 0.5) * 100}% ${(service.photoFocusY ?? 0.5) * 100}%`,
                        }}
                      />
                    ) : (
                      <div
                        key={service.id}
                        className={`absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-muted/40 via-muted/20 to-background transition-opacity duration-700 ${
                          isActive ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <ImageIcon
                          className="h-14 w-14 text-muted-foreground/30"
                          strokeWidth={1}
                        />
                        <span className="px-6 text-center text-sm font-medium uppercase tracking-widest text-muted-foreground/50">
                          {service.title}
                        </span>
                      </div>
                    )
                  })}
                </>
              )}
            </div>

            {/* Text row — on the section bg, sits below the faded image */}
            {!loading && activeService && (
              <div key={activeService.id} className="animate-in fade-in duration-500">
                <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-brand-orange">
                  {activeService.category}
                </p>
                <h3 className="mb-2 text-2xl font-bold tracking-tight">
                  {activeService.title}
                </h3>
                {activeService.description && (
                  <p className="mb-5 max-w-md text-sm leading-relaxed text-muted-foreground line-clamp-3">
                    {activeService.description}
                  </p>
                )}
                <Button asChild size="sm" variant="secondary">
                  <Link to={`/services#${activeService.id}`}>
                    Learn more
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            )}
          </div>

          {/* CTAs — under left column on desktop, after spotlight on mobile */}
          <div className="flex flex-wrap gap-3 md:col-start-1 md:-mt-2">
            <Link to="/services">
              <Button variant="outline">View All Services</Button>
            </Link>
            <Link to="/request">
              <Button>Book Now</Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}
